import { useState, useCallback } from "react";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  type BackupPayload,
  type BackupScope,
  type BackupTableKey,
  type ImportConflictStrategy,
  type RestoreSelection,
  keysPresentInPayload,
  postExport,
  postImport,
  extractMediaUrls,
  postMigrateMediaFile,
  postRewriteMediaUrls,
  formatMediaError,
  RESTORE_SECTION_LABELS,
} from "@/lib/backupApi";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Download, Upload, Loader2, AlertTriangle, History } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const EXPORT_CHOICES: {
  scope: BackupScope;
  label: string;
  description: string;
  requiresStandalone?: boolean;
}[] = [
  {
    scope: "admin_users",
    label: "Utilisateurs admin",
    description: "Auth Supabase, rôles admin/super_admin et profils admin",
  },
  {
    scope: "paths",
    label: "Parcours",
    description: "Parcours, liaisons activités, habitudes liées, contenu d'activité référencé",
  },
  {
    scope: "standalone_activities",
    label: "Activités (catalogue)",
    description: "Toutes les activités et graphes associés (cartes, étapes…)",
    requiresStandalone: true,
  },
  {
    scope: "settings",
    label: "Paramètres",
    description: "Thèmes, thématiques, templates de cartes, réglages",
  },
  {
    scope: "all",
    label: "Tout",
    description: "Inclut les quatre domaines ci-dessus (selon le flag activités). Les données utilisateurs application ne sont pas exportables.",
  },
];

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getEnvironment(): string {
  const env = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  if (env) return env;
  const host = window.location.hostname;
  if (host.includes("localhost") || host.includes("preview")) return "dev";
  if (host.includes("preprod")) return "preprod";
  return "prod";
}

interface ImportHistoryRow {
  id: string;
  imported_at: string;
  admin_email: string | null;
  filename: string | null;
  source_environment: string | null;
  conflict_strategy: string | null;
  ok: boolean;
  errors: string[] | null;
  scopes_included: string[] | null;
}

export function ImportExportManagement() {
  const { enabled: standaloneActivities } = useFeatureFlag("feature_standalone_activities_enabled");
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [exportSelected, setExportSelected] = useState<Set<BackupScope>>(new Set());
  const [importPayload, setImportPayload] = useState<BackupPayload | null>(null);
  const [importFilename, setImportFilename] = useState<string>("");
  const [importRestore, setImportRestore] = useState<RestoreSelection>({});
  const [importConflictStrategy, setImportConflictStrategy] = useState<ImportConflictStrategy>("upsert");
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const [migratingMedia, setMigratingMedia] = useState(false);
  const [mediaTotal, setMediaTotal] = useState(0);
  const [mediaDone, setMediaDone] = useState(0);
  const [mediaUploaded, setMediaUploaded] = useState(0);
  const [mediaSkipped, setMediaSkipped] = useState(0);
  const [mediaErrors, setMediaErrors] = useState<string[]>([]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState<ImportHistoryRow[]>([]);

  const toggleExport = (scope: BackupScope) => {
    setExportSelected((prev) => {
      const next = new Set(prev);
      if (scope === "all") {
        return next.has("all") ? new Set() : new Set<BackupScope>(["all"]);
      }
      next.delete("all");
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const handleExport = async () => {
    const scopes = [...exportSelected];
    if (!scopes.length) {
      toast.error("Sélectionnez au moins une option d'export.");
      return;
    }
    const effectiveScopes: BackupScope[] = scopes.includes("all")
      ? ["all"]
      : scopes.filter((s) => s !== "standalone_activities" || standaloneActivities);
    if (!effectiveScopes.length) {
      toast.error("Sélection vide ou export « Activités » indisponible (feature désactivée).");
      return;
    }
    setExporting(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Session expirée");
      const payload = await postExport(session.access_token, effectiveScopes);
      const env = (payload as Record<string, unknown>).environment as string || getEnvironment();
      const name = `mindsup-backup-${env}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      downloadJson(name, payload);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur d'export");
    } finally {
      setExporting(false);
    }
  };

  const onImportFile = useCallback((file: File | null) => {
    if (!file) return;
    setImportErrors([]);
    setMediaErrors([]);
    setMediaTotal(0);
    setMediaDone(0);
    setMediaUploaded(0);
    setMediaSkipped(0);
    setImportFilename(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = JSON.parse(String(reader.result)) as BackupPayload;
        if (raw.version == null || !raw.exported_at) {
          toast.error("Fichier JSON invalide (métadonnées manquantes).");
          return;
        }
        setImportPayload(raw);
        const keys = keysPresentInPayload(raw);
        const init: RestoreSelection = {};
        for (const k of keys) init[k] = true;
        setImportRestore(init);
        toast.success("Fichier chargé — choisissez les sections à restaurer.");
      } catch {
        toast.error("Impossible de lire le JSON.");
      }
    };
    reader.readAsText(file);
  }, []);

  const toggleRestore = (key: BackupTableKey) => {
    setImportRestore((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const runMediaMigration = async (payload: BackupPayload, accessToken: string) => {
    const urls = extractMediaUrls(payload);
    if (urls.length === 0) return;

    setMigratingMedia(true);
    setMediaTotal(urls.length);
    setMediaDone(0);
    setMediaUploaded(0);
    setMediaSkipped(0);
    setMediaErrors([]);

    let uploaded = 0;
    let skipped = 0;
    const errors: string[] = [];
    const replacements: { oldUrl: string; newUrl: string }[] = [];

    for (const entry of urls) {
      try {
        const result = await postMigrateMediaFile(accessToken, entry);
        if (result.ok && result.newUrl) {
          replacements.push({ oldUrl: entry.originalUrl, newUrl: result.newUrl });
          if (result.status === "uploaded") uploaded++;
          else skipped++;
        } else {
          errors.push(formatMediaError(result.error || `Erreur ${entry.originalUrl}`, entry));
        }
      } catch (e) {
        errors.push(formatMediaError(e instanceof Error ? e.message : `Erreur ${entry.originalUrl}`, entry));
      }
      setMediaDone((prev) => prev + 1);
      setMediaUploaded(uploaded);
      setMediaSkipped(skipped);
      setMediaErrors([...errors]);
    }

    // Rewrite URLs in database
    if (replacements.length > 0) {
      try {
        const rewriteResult = await postRewriteMediaUrls(accessToken, replacements);
        if (rewriteResult.ok) {
          console.log(`[media-rewrite] ${rewriteResult.updated_rows} row(s) updated in DB`);
          if (rewriteResult.warnings?.length) {
            errors.push(...rewriteResult.warnings);
          }
        } else {
          errors.push(`Réécriture URLs en BDD : ${rewriteResult.error}`);
        }
      } catch (e) {
        errors.push(`Réécriture URLs en BDD : ${e instanceof Error ? e.message : String(e)}`);
      }
      setMediaErrors([...errors]);
    }

    setMigratingMedia(false);
    if (errors.length > 0) {
      toast.warning(`Migration médias : ${uploaded} uploadé(s), ${skipped} ignoré(s), ${errors.length} erreur(s)`);
    } else {
      toast.success(`Migration médias terminée : ${uploaded} uploadé(s), ${skipped} déjà présent(s)`);
    }
  };

  const handleImport = async () => {
    if (!importPayload) return;
    const restore = { ...importRestore };
    const any = Object.values(restore).some(Boolean);
    if (!any) {
      toast.error("Cochez au moins une section à importer.");
      return;
    }

    setImportErrors([]);
    setMediaErrors([]);
    setImporting(true);
    setImportProgress(10);
    const progressInterval = setInterval(() => {
      setImportProgress((prev) => (prev >= 90 ? 90 : prev + Math.random() * 8));
    }, 800);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Session expirée");

      await postImport(session.access_token, importPayload, restore, importConflictStrategy, importFilename || undefined);
      clearInterval(progressInterval);
      setImportProgress(100);
      toast.success("Import des données terminé");

      await runMediaMigration(importPayload, session.access_token);

      setImportPayload(null);
      setImportRestore({});
      setImportFilename("");
    } catch (e) {
      clearInterval(progressInterval);
      const message = e instanceof Error ? e.message : "Erreur d'import";
      setImportErrors([message]);
      toast.error(message);
    } finally {
      setTimeout(() => {
        setImporting(false);
        setImportProgress(0);
      }, 600);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from("import_history")
        .select("*")
        .order("imported_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      setHistoryRows((data ?? []) as ImportHistoryRow[]);
    } catch (e) {
      toast.error("Impossible de charger l'historique");
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const STRATEGY_LABELS: Record<string, string> = {
    wipe_then_import: "Supprimer puis importer",
    upsert: "Upsert",
    insert_only: "Insérer seulement",
  };

  const importKeys = importPayload ? keysPresentInPayload(importPayload) : [];

  return (
    <div className="space-y-8">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Mots de passe</AlertTitle>
        <AlertDescription>
          Les mots de passe ne sont pas inclus dans l'export. À l'import, des mots de passe provisoires sont
          générés pour les nouveaux comptes ; utilisez la réinitialisation par email si besoin.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Exporter</CardTitle>
          <CardDescription>
            Télécharge un fichier JSON structuré pour sauvegarde ou migration. Les scopes « parcours » incluent
            le graphe complet des activités référencées.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {EXPORT_CHOICES.map((c) => {
              if (c.requiresStandalone && !standaloneActivities) return null;
              const checked = exportSelected.has(c.scope);
              return (
                <div key={c.scope} className="flex items-start gap-3 rounded-lg border p-3">
                  <Checkbox
                    id={`ex-${c.scope}`}
                    checked={checked}
                    onCheckedChange={() => toggleExport(c.scope)}
                  />
                  <div className="grid gap-1">
                    <Label htmlFor={`ex-${c.scope}`} className="cursor-pointer font-medium">
                      {c.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{c.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={handleExport} disabled={exporting || exportSelected.size === 0}>
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Télécharger le JSON
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Importer</CardTitle>
              <CardDescription>
                Restaure des données à partir d'un export. Les fichiers médias sont migrés un par un après l'import
                des données pour éviter les erreurs de timeout.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={openHistory}>
              <History className="mr-2 h-4 w-4" />
              Historique
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <input
              type="file"
              accept="application/json,.json"
              className="text-sm"
              onChange={(e) => onImportFile(e.target.files?.[0] ?? null)}
            />
          </div>

          {importPayload && (
            <>
              <p className="text-sm text-muted-foreground">
                Export du {importPayload.exported_at} — version {importPayload.version} — scopes :{" "}
                {(importPayload.scopes_included ?? []).join(", ")}
                {(importPayload as Record<string, unknown>).environment && (
                  <> — env : {String((importPayload as Record<string, unknown>).environment)}</>
                )}
              </p>
              <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                <p className="text-sm font-medium">Données déjà en base</p>
                <RadioGroup
                  value={importConflictStrategy}
                  onValueChange={(v) => setImportConflictStrategy(v as ImportConflictStrategy)}
                  className="grid gap-2"
                >
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <RadioGroupItem value="wipe_then_import" id="ics-wipe" className="mt-0.5" />
                    <span>
                      <span className="font-medium">Tout supprimer puis importer</span>
                      <span className="block text-muted-foreground">
                        Vide les tables correspondant aux sections cochées (ordre sécurisé), supprime les comptes Auth
                        listés dans le JSON, puis réimporte. Le plus simple pour rejouer un export complet ou aligner deux
                        environnements sans conflits résiduels.
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <RadioGroupItem value="upsert" id="ics-upsert" className="mt-0.5" />
                    <span>
                      <span className="font-medium">Remplacer si déjà existant</span>
                      <span className="block text-muted-foreground">
                        Upsert par identifiant : met à jour les lignes existantes et crée les autres. Pour certains blocs
                        (items de groupe, transitions entre étapes, liaisons parcours ↔ activités), les lignes
                        existantes sont d&apos;abord retirées sur le périmètre de l&apos;export pour respecter les
                        contraintes d&apos;unicité.
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <RadioGroupItem value="insert_only" id="ics-insert" className="mt-0.5" />
                    <span>
                      <span className="font-medium">Insérer seulement</span>
                      <span className="block text-muted-foreground">
                        N&apos;insère que ce qui n&apos;existe pas encore : mêmes identifiants, ou mêmes emplacements
                        métier (groupe + ordre pour les items de groupe ; étape source + ordre pour les transitions ;
                        parcours + ordre ou parcours + même activité pour les liaisons parcours ↔ activités). Les
                        lignes déjà en base pour ces emplacements sont laissées inchangées (pas de fusion du contenu
                        avec une autre instance qui aurait des UUID différentes).
                      </span>
                    </span>
                  </label>
                </RadioGroup>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {importKeys.map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={`im-${key}`}
                      checked={importRestore[key] === true}
                      onCheckedChange={() => toggleRestore(key)}
                    />
                    <Label htmlFor={`im-${key}`} className="cursor-pointer text-sm font-normal">
                      {RESTORE_SECTION_LABELS[key]}
                    </Label>
                  </div>
                ))}
              </div>
              <Button onClick={handleImport} disabled={importing || migratingMedia}>
                {importing || migratingMedia ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Lancer l'import
              </Button>
              {importing && (
                <div className="space-y-1">
                  <Progress value={importProgress} className="h-2" />
                  <p className="text-xs text-muted-foreground">Import des données en cours…</p>
                </div>
              )}
              {migratingMedia && (
                <div className="space-y-1">
                  <Progress value={mediaTotal > 0 ? (mediaDone / mediaTotal) * 100 : 0} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    Migration des médias : {mediaDone}/{mediaTotal} — {mediaUploaded} uploadé(s), {mediaSkipped} ignoré(s)
                    {mediaErrors.length > 0 && `, ${mediaErrors.length} erreur(s)`}
                  </p>
                </div>
              )}
            </>
          )}

          {importErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Erreurs d'import</AlertTitle>
              <AlertDescription className="mt-1 max-h-40 overflow-auto text-xs whitespace-pre-wrap">
                {importErrors.join("\n")}
              </AlertDescription>
            </Alert>
          )}

          {!migratingMedia && mediaErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Erreurs de migration médias</AlertTitle>
              <AlertDescription className="mt-1 max-h-40 overflow-auto text-xs whitespace-pre-wrap">
                {mediaErrors.join("\n")}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Historique des imports</DialogTitle>
            <DialogDescription>Liste des imports effectués depuis l'interface d'administration.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : historyRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Aucun import enregistré.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Env. source</TableHead>
                  <TableHead>Stratégie</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(row.imported_at).toLocaleString("fr-FR")}
                    </TableCell>
                    <TableCell className="text-xs">{row.admin_email ?? "—"}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" title={row.filename ?? undefined}>
                      {row.filename ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">{row.source_environment ?? "—"}</TableCell>
                    <TableCell className="text-xs">
                      {row.conflict_strategy ? STRATEGY_LABELS[row.conflict_strategy] || row.conflict_strategy : "—"}
                    </TableCell>
                    <TableCell>
                      {row.ok ? (
                        <Badge variant="default" className="text-xs">OK</Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">Erreur</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
